import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { TaxGroupService } from "../../../services/user/taxgroups.services";
import { handleNotify, TOASTER_TYPE, TOASTER_POSITION } from "../../common/notification/toaster_notify.component";
import { useSelector } from "react-redux";
import Select from 'react-select';
import { TAX_TYPE_ENUM } from "../../../utility";

const AddTaxGroupComponent = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const taxGroupId = location.state?.taxGroupId || 0;
    const isEditMode = !!taxGroupId;
    const selectedStore = useSelector((state) => state?.user?.selectedStoreId);
    const [loading, setLoading] = useState(false);

    const taxTypeOptions = [
        { value: 'Intra State', label: 'Intra State' },
        { value: 'Inter State', label: 'Inter State' }
    ];

    const taxTypeEnumOptions = TAX_TYPE_ENUM.map(group => ({
        label: group.name,
        value: group.name
    }));

    const formik = useFormik({
        initialValues: {
            taxGroupId: 1,
            taxGroupName: "",
            taxType: "Intra State",
            totalGST: 0.00,
            CGST: 0.00,
            SGST: 0.00,
            IGST: 0.00,
            isActive: true,
            storeId: selectedStore,
        },
        validationSchema: Yup.object({
            taxGroupName: Yup.string().required("Please enter a valid name"),
            totalGST: Yup.number().typeError("Total GST must be a valid number").required("Please enter total GST"),
            CGST: Yup.number().typeError("CGST must be a valid number").required("Please enter CGST"),
            SGST: Yup.number().typeError("SGST must be a valid number").required("Please enter SGST"),
            IGST: Yup.number().typeError("IGST must be a valid number").required("Please enter IGST"),
        }),
        onSubmit: async (values) => {
            try {
                setLoading(true);
                const data = {
                    ...values,
                    totalGST: parseFloat(values.totalGST).toFixed(2),
                    CGST: parseFloat(values.CGST).toFixed(2),
                    SGST: parseFloat(values.SGST).toFixed(2),
                    IGST: parseFloat(values.IGST).toFixed(2),
                    isActive: values.isActive === true ? true : false
                };

                if (isEditMode) {
                    await TaxGroupService.updateTaxGroupById(taxGroupId, data)
                        .then((response) => {
                            if (response) {
                                navigate("/taxgroup");
                            }
                        });
                } else {
                    await TaxGroupService.addNewTaxGroup(data)
                        .then((response) => {
                            if (response) {
                                navigate("/taxgroup");
                            }
                        });
                }
            } catch (error) {
                console.error("Error while submitting form:", error);
                handleNotify(
                    "An error occurred while saving the tax group.",
                    TOASTER_TYPE.ERROR,
                    TOASTER_POSITION.TOP_RIGHT
                );
            } finally {
                setLoading(false);
            }
        },
    });


    useEffect(() => {
        if (isEditMode) {
            const fetchTaxGroupDetail = async () => {
                try {
                    const data = await TaxGroupService.getTaxGroupById(taxGroupId)
                    formik.setValues({
                        taxGroupName: data?.taxGroupName,
                        taxType: data?.taxType,
                        totalGST: data?.totalGST,
                        CGST: data?.CGST,
                        SGST: data?.SGST,
                        IGST: data?.IGST,
                        isActive: data?.isActive,
                    })
                }
                catch (error) {
                    console.log('Error fetching tax group details', error)
                }
            }
            fetchTaxGroupDetail()
        }
    }, [])

    const handleGSTChange = (field, value) => {
        formik.setFieldValue(field, value)

        const CGST = parseFloat(formik.values.CGST) || 0;
        const SGST = parseFloat(formik.values.SGST) || 0;
        const IGST = parseFloat(formik.values.IGST) || 0;

        const totalGST = CGST + SGST + IGST

        formik.setFieldValue('totalGST', totalGST.toFixed(2))
    };

    const handleBackClick = () => {
        navigate("/taxgroup");
    };

    return (
        <div className="page-wrapper m-0">
            <div className="page-content">
                <div className="page-breadcrumb d-flex align-items-center mb-3">
                    <div
                        className="breadcrumb-title pe-3 text-primary fw-bold fs-4 cursor-pointer"
                        onClick={handleBackClick}
                    >
                        Tax Groups</div>
                    <div className="ps-3">
                        <nav aria-label="breadcrumb">
                            <ol className="breadcrumb mb-0 p-0">
                                <li className="breadcrumb-item active" aria-current="page">
                                    {isEditMode ? "Edit Tax Group" : "New Tax Group"}
                                </li>
                            </ol>
                        </nav>
                    </div>
                    <div className="ms-auto">
                        <button
                            type="button"
                            className="btn btn-outline-secondary me-2 px-4 py-2 rounded-3 shadow-sm hover-effect"
                            onClick={handleBackClick}
                        >
                            Back
                        </button>
                    </div>
                </div>

                <div className="card radius-10 border-top border-0 border-4 border-primary p-4">
                    <div className="card-body form-container">
                        <form onSubmit={formik.handleSubmit} className="mt-4">
                            <div className="row mb-4">
                                <div className="col-md-6 col-12">
                                    <label htmlFor="taxGroupName" className="form-label d-flex">
                                        Name <span className="text-danger">*</span>:
                                    </label>
                                    <input
                                        type="text"
                                        id="taxGroupName"
                                        name="taxGroupName"
                                        className={`form-control ${formik.touched.taxGroupName && formik.errors.taxGroupName ? "is-invalid" : ""}`}
                                        placeholder="Enter Name"
                                        value={formik.values.taxGroupName}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                    />
                                    {formik.touched.taxGroupName && formik.errors.taxGroupName && (
                                        <div className="invalid-feedback d-flex">{formik.errors.taxGroupName}</div>
                                    )}
                                </div>
                                <div className="col-md-6 col-12 mb-3">
                                    <label htmlFor="taxType" className="form-label d-flex">
                                        Type:
                                    </label>
                                    <div className="text-start">
                                        <Select
                                            id="taxType"
                                            name="taxType"
                                            options={taxTypeEnumOptions}
                                            value={taxTypeEnumOptions.find(option => option.value === formik.values.taxType)}
                                            onChange={selectedOption => formik.setFieldValue('taxType', selectedOption.value)}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* GST Fields */}
                            <div className="row mb-4">
                                <div className="col-md-3 col-12">
                                    <label htmlFor="CGST" className="form-label d-flex">
                                        CGST (%) <span className="text-danger">*</span>:
                                    </label>
                                    <input
                                        type="number"
                                        id="CGST"
                                        name="CGST"
                                        placeholder="0.00"
                                        className={`form-control ${formik.touched.CGST && formik.errors.CGST ? "is-invalid" : ""}`}
                                        value={formik.values.CGST}
                                        onChange={(e) => handleGSTChange("CGST", e.target.value)}
                                        onBlur={formik.handleBlur}
                                    />
                                    {formik.touched.CGST && formik.errors.CGST && (
                                        <div className="invalid-feedback d-flex">{formik.errors.CGST}</div>
                                    )}
                                </div>

                                <div className="col-md-3 col-12">
                                    <label htmlFor="SGST" className="form-label d-flex">
                                        SGST (%) <span className="text-danger">*</span>:
                                    </label>
                                    <input
                                        type="number"
                                        id="SGST"
                                        name="SGST"
                                        placeholder="0.00"
                                        className={`form-control ${formik.touched.SGST && formik.errors.SGST ? "is-invalid" : ""}`}
                                        value={formik.values.SGST}
                                        onChange={(e) => handleGSTChange("SGST", e.target.value)}
                                        onBlur={formik.handleBlur}
                                    />
                                    {formik.touched.SGST && formik.errors.SGST && (
                                        <div className="invalid-feedback d-flex">{formik.errors.SGST}</div>
                                    )}
                                </div>

                                <div className="col-md-3 col-12 mb-3">
                                    <label htmlFor="IGST" className="form-label d-flex">
                                        IGST (%) <span className="text-danger">*</span>:
                                    </label>
                                    <input
                                        type="number"
                                        id="IGST"
                                        name="IGST"
                                        placeholder="0.00"
                                        className={`form-control ${formik.touched.IGST && formik.errors.IGST ? "is-invalid" : ""}`}
                                        value={formik.values.IGST}
                                        onChange={(e) => handleGSTChange("IGST", e.target.value)}
                                        onBlur={formik.handleBlur}
                                    />
                                    {formik.touched.IGST && formik.errors.IGST && (
                                        <div className="invalid-feedback d-flex">{formik.errors.IGST}</div>
                                    )}
                                </div>
                                <div className="col-md-3 col-12">
                                    <label htmlFor="totalGST" className="form-label d-flex">
                                        Total GST (%) <span className="text-danger">*</span>:
                                    </label>
                                    <input
                                        type="number"
                                        id="totalGST"
                                        name="totalGST"
                                        placeholder="0.00"
                                        className={`form-control ${formik.touched.totalGST && formik.errors.totalGST ? "is-invalid" : ""}`}
                                        value={formik.values.totalGST}
                                        readOnly
                                    />
                                    {formik.touched.totalGST && formik.errors.totalGST && (
                                        <div className="invalid-feedback d-flex">{formik.errors.totalGST}</div>
                                    )}
                                </div>
                            </div>

                            {/* Status Radio Buttons */}
                            <div className="d-flex align-items-center mb-4">
                                <label className="form-label me-3">Status:</label>
                                <div className="form-check form-check-inline me-3">
                                    <input
                                        type="radio"
                                        id="isActive"
                                        name="isActive"
                                        className="form-check-input"
                                        value={true}
                                        checked={formik.values.isActive === true}
                                        onChange={() => formik.setFieldValue("isActive", true)}
                                    />
                                    <label className="form-check-label" htmlFor="active">
                                        Active
                                    </label>
                                </div>
                                <div className="form-check form-check-inline">
                                    <input
                                        type="radio"
                                        id="inactive"
                                        name="isActive"
                                        className="form-check-input"
                                        value={false}
                                        checked={formik.values.isActive === false}
                                        onChange={() => formik.setFieldValue("isActive", false)}
                                    />
                                    <label className="form-check-label" htmlFor="inactive">
                                        Inactive
                                    </label>
                                </div>
                            </div>

                            {/* Submit Button */}
                            <hr />
                            <div className="d-flex flex-column flex-md-row mt-4 justify-content-end">
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary mb-2 mb-md-0 me-2 px-4 py-2 rounded-3 shadow-sm hover-effect"
                                    onClick={handleBackClick}
                                >
                                    Back
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-primary px-4 py-2 rounded-3 shadow-sm hover-effect"
                                    disabled={loading}
                                >
                                    {loading ? "Saving..." : isEditMode ? "Update" : "Save"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddTaxGroupComponent;
